import React, { useMemo } from 'react';
import { useCardPermission } from 'hooks/useCardPermission';
import { getCardConfig } from '../../config/cardPermissions';

export interface PermissionWrapperProps {
  /**
   * Card identifier - used to look up permissions from config
   * If provided, wrapper will only render children if user has permission
   */
  cardId: string;
  /**
   * Optional: Override permissions for this specific instance
   * If provided, this takes precedence over config
   */
  permissions?: number[];
  /**
   * Optional: Override feature flag key for this specific instance
   * If provided, this takes precedence over config
   */
  featureFlagKey?: string;
  /**
   * Children to render if user has permission
   */
  children: React.ReactNode;
  /**
   * Optional: Fallback content to show if user doesn't have permission
   * If not provided, nothing is rendered
   */
  fallback?: React.ReactNode;
}

/**
 * PermissionWrapper - A wrapper component that conditionally renders children based on permissions
 * 
 * This allows to add permissions to existing components without modifying them.
 * 
 * Usage:
 * ```tsx
 * <PermissionWrapper cardId="sales-dashboard">
 *   <Card bg={bgColor} p="15px">
 *     <SalesDashboard />
 *   </Card>
 * </PermissionWrapper>
 * ```
 * 
 * Or with inline permissions:
 * ```tsx
 * <PermissionWrapper 
 *   cardId="custom-card" 
 *   permissions={[ROLE_ADMIN, ROLE_MANAGER]}
 *   featureFlagKey="enable-custom-feature"
 * >
 *   <ExistingComponent to wrap />
 * </PermissionWrapper>
 * ```
 */
export const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  cardId,
  permissions: overridePermissions,
  featureFlagKey: overrideFeatureFlagKey,
  children,
  fallback = null,
}) => {
  const config = useMemo(() => getCardConfig(cardId), [cardId]);
  
  const { permissions, featureFlagKey } = useMemo(() => ({
    permissions: overridePermissions || config?.permissions || [],
    featureFlagKey: overrideFeatureFlagKey || config?.featureFlagKey,
  }), [overridePermissions, overrideFeatureFlagKey, config]);

  const canView = useCardPermission(permissions, featureFlagKey);

  if (!canView) {
    return <>{fallback}</>;
  }

  if (children === null || children === undefined) {
    return null;
  }

  if (React.isValidElement(children) && children.type === null) {
    return null;
  }

  return <>{children}</>;
};